/**
 * Claude Code 素色 ECharts 主题
 *
 * 设计原则：
 *   - 单色阶为主：墨黑 → 灰阶 → 赤陶橙
 *   - 多对比图最多 2 色（墨黑 + 橙）
 *   - 数字与文字全部 Geist Mono / Geist
 *   - 极淡网格线 / 1px 轴线 / 无渐变
 *
 * 使用方式：
 *   1. 全局注册：echarts.registerTheme('claude-mono', claudeMonoTheme)
 *   2. 组件引用：v-chart :theme="'claude-mono'" 或 init(el, 'claude-mono')
 *   3. 或直接：option.color = CLAUDE_PALETTE
 */

/** 主色阶 —— 7 色（最常用） */
export const CLAUDE_PALETTE = [
  '#1F1E1D', // 墨黑（主线）
  '#CC785C', // 赤陶橙（强调线）
  '#8A8780', // 暖灰
  '#3F6C4D', // 苔绿（成功）
  '#A14B3A', // 砖红（异常）
  '#4A7088', // 灰蓝（信息）
  '#6B5B8E', // 灰紫
] as const

/** 单色阶 sequential —— 用于热力图 / 区间图 / 渐变 */
export const CLAUDE_SEQUENTIAL = [
  '#FAF1ED',
  '#F4E8E2',
  '#EFD4C4',
  '#E5BBA9',
  '#DBA194',
  '#CC785C',
  '#B5664A',
  '#9D5640',
] as const

/** 对比双色 —— 仅当真的需要两条线时（实际 vs 预测，正常 vs 异常） */
export const CLAUDE_DUAL = ['#1F1E1D', '#CC785C'] as const

/** ECharts 完整主题对象 */
export const claudeMonoTheme = {
  color: [...CLAUDE_PALETTE],
  backgroundColor: 'transparent',

  textStyle: {
    fontFamily: 'Geist, Geist Sans, Inter, -apple-system, PingFang SC, sans-serif',
    color: '#1F1E1D',
  },

  title: {
    textStyle: { color: '#1F1E1D', fontWeight: 600, fontSize: 15 },
    subtextStyle: { color: '#8A8780', fontSize: 12 },
  },

  legend: {
    textStyle: { color: '#4A4845', fontSize: 12 },
    icon: 'roundRect',
    itemGap: 16,
  },

  grid: {
    borderColor: '#E5E2D9',
    borderWidth: 0,
    containLabel: true,
  },

  xAxis: {
    axisLine: { lineStyle: { color: '#D6D2C6', width: 1 } },
    splitLine: { show: false },
    axisLabel: { color: '#8A8780', fontSize: 11, fontFamily: 'Geist Mono, JetBrains Mono, monospace' },
    axisTick: { lineStyle: { color: '#D6D2C6' } },
  },

  yAxis: {
    axisLine: { show: false },
    splitLine: { lineStyle: { color: '#EDEBE3', type: 'dashed', width: 1 } },
    axisLabel: { color: '#8A8780', fontSize: 11, fontFamily: 'Geist Mono, JetBrains Mono, monospace' },
    axisTick: { show: false },
  },

  // 折线图：默认线宽 2px / 平滑
  line: {
    lineStyle: { width: 2 },
    symbolSize: 6,
    smooth: false,
    showSymbol: false,
  },

  // 柱状图：圆角 4px
  bar: {
    itemStyle: { borderRadius: [4, 4, 0, 0] },
  },

  // 饼图
  pie: {
    itemStyle: { borderColor: '#FAF9F5', borderWidth: 2 },
    label: { color: '#4A4845', fontSize: 12 },
    labelLine: { lineStyle: { color: '#D6D2C6' } },
  },

  // 散点
  scatter: { symbolSize: 8 },

  // tooltip：暖白底 + 1px 边框 + 极淡阴影
  tooltip: {
    backgroundColor: '#FAF9F5',
    borderColor: '#E5E2D9',
    borderWidth: 1,
    textStyle: { color: '#1F1E1D', fontSize: 12, fontFamily: 'Geist, Inter, sans-serif' },
    extraCssText: 'box-shadow: 0 4px 12px -2px rgba(31,30,29,0.08); border-radius: 8px;',
  },

  // 数据缩放轴
  dataZoom: {
    backgroundColor: 'transparent',
    fillerColor: 'rgba(204, 120, 92, 0.15)',
    borderColor: '#E5E2D9',
    handleStyle: { color: '#CC785C', borderColor: '#9D5640' },
    textStyle: { color: '#8A8780' },
  },

  // 视觉映射（热力图等）
  visualMap: {
    inRange: { color: [...CLAUDE_SEQUENTIAL] },
    textStyle: { color: '#4A4845' },
  },

  // markLine / markArea
  markLine: {
    lineStyle: { color: '#CC785C', type: 'dashed', width: 1 },
    label: { color: '#9D5640', fontSize: 11 },
  },
} as const

export const CLAUDE_THEME_NAME = 'claude-mono'
